import { Badge } from "@/components/ui/badge";

function CustomerInfo({ order, settings, prefix }) {
  const showName = settings?.[`${prefix}_show_customer_name`] !== false;
  const showAddr = settings?.[`${prefix}_show_customer_address`];
  const showPhone = settings?.[`${prefix}_show_customer_phone`];
  if (!order?.customer_name && !order?.customer_address && !order?.customer_phone) return null;
  return (
    <div className="mb-2">
      {showName && order?.customer_name && <p className="font-bold">{order.customer_name}</p>}
      {showAddr && order?.customer_address && <p className="text-[10px]">{order.customer_address}</p>}
      {showPhone && order?.customer_phone && <p className="text-[10px]">{order.customer_phone}</p>}
    </div>
  );
}

export function CustomerReceipt({ order, settings }) {
  const sym = settings?.currency_symbol || "$";
  const brandName = settings?.brand_name || "CashUpPOS";
  const address = settings?.business_address || "";
  const phone = settings?.business_phone || "";
  const footer = settings?.receipt_footer || "Thank you for your business!";
  const header = settings?.receipt_header || "";

  const orderDate = order?.created_at ? new Date(order.created_at) : new Date();
  const scheduled = order?.scheduled_time ? new Date(order.scheduled_time) : null;

  return (
    <div className="bg-white font-mono text-[11px] leading-tight w-[300px] mx-auto p-4 border border-slate-200 shadow-sm" data-testid="customer-receipt">
      <div className="text-center mb-3">
        <p className="text-base font-bold">{brandName}</p>
        {address && <p className="text-[10px] text-slate-600">{address}</p>}
        {phone && <p className="text-[10px] text-slate-600">{phone}</p>}
        {header && <p className="text-[10px] text-slate-500 mt-1">{header}</p>}
      </div>
      <div className="border-t border-dashed border-slate-300 my-2" />
      <div className="flex justify-between mb-1">
        <span>Order: <strong>{order?.ticket_number || order?.order_id?.slice(-8) || "—"}</strong></span>
        <span>{order?.dining_type_code || ""}</span>
      </div>
      <div className="flex justify-between mb-1">
        <span>{orderDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
        <span>{orderDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
      </div>
      {order?.employee_name && <p className="mb-1">Cashier: {order.employee_name}</p>}
      <CustomerInfo order={order} settings={settings} prefix="receipt" />
      {scheduled && (
        <p className="font-bold text-center bg-slate-100 px-2 py-1 rounded mb-1">
          READY AT: {scheduled.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
        </p>
      )}
      <div className="border-t border-dashed border-slate-300 my-2" />
      <div className="space-y-1.5 mb-2">
        {(order?.items || []).map((item, idx) => (
          <div key={idx}>
            <div className="flex justify-between">
              <span className="flex-1">{item.quantity > 1 ? `${item.quantity}x ` : ""}{item.name}</span>
              <span className="ml-2">{sym}{(item.price * item.quantity).toFixed(2)}</span>
            </div>
            {item.config_desc && <p className="text-[9px] text-slate-500 pl-3">{item.config_desc}</p>}
            {item.is_combo && item.combo_items?.map((ci, j) => (
              <p key={j} className="text-[9px] text-slate-500 pl-3">+ {ci.name}{ci.config_desc ? ` (${ci.config_desc})` : ""}</p>
            ))}
            {item.included_items?.map((inc, j) => (
              <p key={j} className="text-[9px] text-slate-500 pl-3">
                {inc.included_qty + (inc.extra_qty || 0)}x {inc.name}
                {inc.extra_qty > 0 && ` (+${inc.extra_qty} extra ${sym}${(inc.extra_cost || 0).toFixed(2)})`}
              </p>
            ))}
          </div>
        ))}
      </div>
      <div className="border-t border-dashed border-slate-300 my-2" />
      <div className="space-y-0.5">
        <div className="flex justify-between"><span>Subtotal</span><span>{sym}{(order?.subtotal || 0).toFixed(2)}</span></div>
        {(order?.discount || 0) > 0 && (
          <div className="flex justify-between text-emerald-700"><span>Discount</span><span>-{sym}{(order.discount).toFixed(2)}</span></div>
        )}
        {order?.applied_specials?.map((s, i) => (
          <div key={i} className="flex justify-between text-[10px] text-amber-700 pl-2">
            <span>{s.name}</span>
            <span>-{s.type === "percentage" ? `${s.amount}%` : `${sym}${(s.amount || 0).toFixed(2)}`}</span>
          </div>
        ))}
        <div className="flex justify-between"><span>Tax</span><span>{sym}{(order?.tax || 0).toFixed(2)}</span></div>
        <div className="border-t border-slate-300 my-1" />
        <div className="flex justify-between text-sm font-bold"><span>TOTAL</span><span>{sym}{(order?.total || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Payment</span><span className="uppercase">{order?.payment_method || "cash"}</span></div>
      </div>
      <div className="border-t border-dashed border-slate-300 my-2" />
      <div className="text-center text-[10px] text-slate-500">
        {footer.split("\n").map((line, i) => <p key={i}>{line}</p>)}
      </div>
    </div>
  );
}

export function KitchenTicket({ order, settings }) {
  const orderDate = order?.created_at ? new Date(order.created_at) : new Date();
  const scheduled = order?.scheduled_time ? new Date(order.scheduled_time) : null;

  return (
    <div className="bg-white font-mono text-sm leading-snug w-[300px] mx-auto p-4 border border-slate-200 shadow-sm" data-testid="kitchen-ticket">
      <div className="text-center mb-2">
        <p className="text-xl font-black tracking-widest">{order?.dining_type_code || "ORDER"}</p>
        <p className="text-2xl font-black">{order?.ticket_number || order?.order_id?.slice(-8) || "—"}</p>
        <p className="text-xs text-slate-600">{orderDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</p>
      </div>
      {scheduled && (
        <div className="text-center bg-black text-white px-3 py-2 rounded mb-2" data-testid="kitchen-scheduled-time">
          <p className="text-xs font-bold tracking-wider">READY AT</p>
          <p className="text-2xl font-black underline decoration-2">{scheduled.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</p>
        </div>
      )}
      <CustomerInfo order={order} settings={settings} prefix="kitchen" />
      <div className="border-t-2 border-black my-2" />
      <div className="space-y-2">
        {(order?.items || []).map((item, idx) => (
          <div key={idx} className="border-b border-dashed border-slate-300 pb-1.5">
            <p className="text-base font-bold">
              {item.quantity > 1 && <span className="text-lg">{item.quantity}x </span>}
              {item.name}
            </p>
            {item.size_name && <p className="text-sm font-semibold pl-2">({item.size_name})</p>}
            {item.modifiers?.map((mod, j) => {
              const placement = mod.placement === "side_a" ? " [A]" : mod.placement === "side_b" ? " [B]" : "";
              const qty = mod.qty > 1 ? `${mod.qty}x ` : "";
              return <p key={j} className="pl-3 text-sm">{qty}{mod.name}{placement}</p>;
            })}
            {item.is_combo && item.combo_items?.map((ci, j) => (
              <p key={j} className="pl-3 text-sm font-medium">+ {ci.name}{ci.config_desc ? ` (${ci.config_desc})` : ""}</p>
            ))}
            {item.included_items?.map((inc, j) => (
              <p key={j} className="pl-3 text-xs text-slate-600">
                {inc.included_qty + (inc.extra_qty || 0)}x {inc.name}
                {inc.extra_qty > 0 && ` (+${inc.extra_qty} extra)`}
              </p>
            ))}
          </div>
        ))}
      </div>
      {order?.notes && (
        <>
          <div className="border-t-2 border-black my-2" />
          <p className="font-bold text-sm">NOTES: {order.notes}</p>
        </>
      )}
    </div>
  );
}

export function DeliveryTicket({ order, settings }) {
  const sym = settings?.currency_symbol || "$";
  const brandName = settings?.brand_name || "CashUpPOS";
  const orderDate = order?.created_at ? new Date(order.created_at) : new Date();
  const scheduled = order?.scheduled_time ? new Date(order.scheduled_time) : null;

  return (
    <div className="bg-white font-mono text-[11px] leading-tight w-[300px] mx-auto p-4 border border-slate-200 shadow-sm" data-testid="delivery-ticket">
      {/* Delivery header */}
      <div className="text-center mb-2">
        <p className="text-lg font-black tracking-widest">DELIVERY</p>
        <p className="text-base font-bold">{brandName}</p>
      </div>

      {/* Prominent customer / delivery info */}
      <div className="bg-slate-100 border-2 border-black rounded p-3 mb-3" data-testid="delivery-address-block">
        {order?.customer_name && <p className="text-base font-black">{order.customer_name}</p>}
        {order?.customer_address && <p className="text-sm font-bold mt-0.5">{order.customer_address}</p>}
        {order?.customer_phone && <p className="text-sm font-semibold mt-0.5">{order.customer_phone}</p>}
        {!order?.customer_name && !order?.customer_address && (
          <p className="text-sm text-slate-400 text-center">No delivery info</p>
        )}
      </div>

      {/* Scheduled time */}
      {scheduled && (
        <div className="text-center bg-black text-white px-3 py-2 rounded mb-2" data-testid="delivery-scheduled-time">
          <p className="text-xs font-bold tracking-wider">DELIVER BY</p>
          <p className="text-xl font-black underline decoration-2">{scheduled.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</p>
        </div>
      )}

      {/* Order info */}
      <div className="flex justify-between mb-1">
        <span>Order: <strong>{order?.ticket_number || order?.order_id?.slice(-8) || "—"}</strong></span>
        <span>{orderDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</span>
      </div>
      <div className="border-t border-dashed border-slate-300 my-2" />

      {/* Items with checkboxes */}
      <div className="space-y-1.5 mb-2">
        {(order?.items || []).map((item, idx) => (
          <div key={idx} className="flex gap-2">
            <div className="flex-shrink-0 mt-0.5">
              <div className="w-4 h-4 border-2 border-black rounded-sm" data-testid={`delivery-check-${idx}`} />
            </div>
            <div className="flex-1">
              <div className="flex justify-between">
                <span className="font-bold">{item.quantity > 1 ? `${item.quantity}x ` : ""}{item.name}</span>
                <span className="ml-1">{sym}{(item.price * item.quantity).toFixed(2)}</span>
              </div>
              {item.config_desc && <p className="text-[9px] text-slate-500">{item.config_desc}</p>}
              {item.is_combo && item.combo_items?.map((ci, j) => (
                <div key={j} className="flex gap-2 ml-2">
                  <div className="w-3 h-3 border border-slate-400 rounded-sm mt-0.5 flex-shrink-0" />
                  <p className="text-[9px] text-slate-600">{ci.name}{ci.config_desc ? ` (${ci.config_desc})` : ""}</p>
                </div>
              ))}
              {item.included_items?.map((inc, j) => (
                <div key={j} className="flex gap-2 ml-2">
                  <div className="w-3 h-3 border border-slate-400 rounded-sm mt-0.5 flex-shrink-0" />
                  <p className="text-[9px] text-slate-600">
                    {inc.included_qty + (inc.extra_qty || 0)}x {inc.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-dashed border-slate-300 my-2" />

      {/* Totals */}
      <div className="space-y-0.5">
        <div className="flex justify-between"><span>Subtotal</span><span>{sym}{(order?.subtotal || 0).toFixed(2)}</span></div>
        {(order?.discount || 0) > 0 && (
          <div className="flex justify-between"><span>Discount</span><span>-{sym}{(order.discount).toFixed(2)}</span></div>
        )}
        <div className="flex justify-between"><span>Tax</span><span>{sym}{(order?.tax || 0).toFixed(2)}</span></div>
        <div className="border-t border-slate-300 my-1" />
        <div className="flex justify-between text-sm font-bold"><span>TOTAL</span><span>{sym}{(order?.total || 0).toFixed(2)}</span></div>
        <div className="flex justify-between"><span>Payment</span><span className="uppercase">{order?.payment_method || "cash"}</span></div>
      </div>

      {/* Notes */}
      {order?.notes && (
        <>
          <div className="border-t border-dashed border-slate-300 my-2" />
          <p className="font-bold text-[10px]">NOTES: {order.notes}</p>
        </>
      )}

      {/* Signature line */}
      <div className="border-t border-dashed border-slate-300 my-3" />
      <div className="mt-6 border-t border-black pt-1">
        <p className="text-[9px] text-slate-400 text-center">Customer Signature</p>
      </div>
    </div>
  );
}
